// Submission status semantics, in one place and on the design system's status tokens.
//
// The old treatment hard-coded raw palette classes (green-600 / orange-500 / purple-500) in six
// components, which the design system forbids: colour comes from `success`, `warning`, `info` and
// `destructive`, each with its own foreground. Every status indicator - dot, pill, card border, row
// strip - reads its tone from here, so a row and its own drawer can never disagree.
//
// None of these tokens is ink. `--success`, `--warning` and `--info` are surfaces with a paired
// `-foreground`, and painted as text on a card they land around 2.4:1 - under even the 3:1 a
// graphic needs - the same class of defect the design system's ink sweep fixed for `--primary` and
// `--destructive`. So a status word is a Badge (toneBadge) and a status mark is an icon
// (StatusIcon, whose shape carries the tone where no ink exists) or a filled chip (TONE_BADGE),
// which carries its own foreground.

export type SubmissionStatus =
  | 'sent to client'
  | 'in hopper'
  | 'pending'
  | 'escalated'
  | 'pending qc review'
  | 'held for p0'
  | 'delivery failed'
  | 'not sent - removed'
  | 'removed';

export type StatusTone =
  'success' | 'warning' | 'delayed' | 'waiting' | 'info' | 'destructive' | 'muted';

const STATUS_TONE: Record<SubmissionStatus, StatusTone> = {
  'sent to client': 'success',
  'in hopper': 'warning',
  pending: 'warning',
  escalated: 'info',
  'pending qc review': 'info',
  'held for p0': 'waiting',
  'delivery failed': 'destructive',
  // The pre-submission liveness check found the content already gone before it was ever sent - same
  // treatment as an outright delivery failure, so it reads as unmistakably not-delivered.
  'not sent - removed': 'destructive',
  // Taken out of the pipeline on purpose - rejected in QC or removed by hand - so neither a failure
  // nor still in flight.
  removed: 'muted'
};

export const STATUS_LABEL: Record<SubmissionStatus, string> = {
  'sent to client': 'Sent',
  'in hopper': 'In Hopper',
  pending: 'Pending',
  escalated: 'Escalated',
  'pending qc review': 'Pending QC',
  'held for p0': 'Held for P0',
  'delivery failed': 'Delivery Failed',
  'not sent - removed': 'Not Sent — Removed',
  removed: 'Removed'
};

export function statusTone(status: string | null | undefined): StatusTone {
  return (status && (STATUS_TONE as Record<string, StatusTone>)[status]) || 'muted';
}

export function statusLabel(status: string | null | undefined): string {
  return (status && (STATUS_LABEL as Record<string, string>)[status]) || '—';
}

// A job run's status (the API's `Job.status`). Timing out is not a failure of the job's own work and
// not a success either, so it is delayed: an amber badge, like a warning, with a clock icon (the same
// clock as a pending job's `waiting`, whose badge is grey, so the badge colour and label tell them
// apart). It needs a look, and it must read the same in the rail, the Jobs table, the Collections
// run history and the job page.
const JOB_STATUS_TONE: Record<string, StatusTone> = {
  pending: 'waiting',
  running: 'info',
  completed: 'success',
  failed: 'destructive',
  timed_out: 'delayed'
};

export const JOB_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  running: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  timed_out: 'Timed out'
};

export function jobStatusTone(status: string | null | undefined): StatusTone {
  return (status && JOB_STATUS_TONE[status]) || 'muted';
}

export function jobStatusLabel(status: string | null | undefined): string {
  return (status && JOB_STATUS_LABEL[status]) || '—';
}

// The platform re-check's own result for a submission (available / restricted / removed /
// unknown), mapped once so the grid's status cell and the detail drawer's banner paint the same
// icon for the same answer.
const PLATFORM_STATUS_TONE: Record<string, StatusTone> = {
  available: 'success',
  restricted: 'warning',
  removed: 'destructive'
};

export function platformStatusTone(status: string | null | undefined): StatusTone {
  return (status && PLATFORM_STATUS_TONE[status.toLowerCase()]) || 'muted';
}

// A YouTube entity's own visibility (public / unlisted / private), as the Single Submission status
// cards show it. An unrecognised value still reads, just without a tone.
const VISIBILITY_TONE: Record<string, StatusTone> = {
  public: 'success',
  unlisted: 'warning',
  private: 'destructive'
};

export function visibilityTone(status: string | null | undefined): StatusTone {
  return (status && VISIBILITY_TONE[status.toLowerCase()]) || 'muted';
}

// The Badge props for each tone. ui-components-react 4.0 dropped Badge's `success` / `warning` /
// `info` variants (the design keeps destructive as its one status colour), but kept the tokens, and
// PRI's status words still need their colour: "Sent" beside "In Hopper" is how an analyst avoids
// re-submitting. So those three are a `secondary` Badge painted with the tone's own surface and
// foreground (TONE_BADGE, below), which is exactly what the removed variants painted. Muted is
// painted too: from 4.0.0-rc.5 a bare `secondary` Badge is a mid-grey fill with inverted ink, which
// would make "no status" the loudest chip on the row.
export type ToneBadgeProps = {
  readonly variant: 'secondary' | 'destructive';
  readonly className?: string;
};

export function toneBadge(tone: StatusTone): ToneBadgeProps {
  if (tone === 'destructive') {
    return {variant: 'destructive'};
  }

  return {variant: 'secondary', className: TONE_BADGE[tone]};
}

// The same treatment as a class string, for the places that paint a status onto something that is
// not a Badge (a card corner, a timeline marker). Destructive is tinted rather than filled, matching
// the design system's own destructive treatment.
export const TONE_BADGE: Record<StatusTone, string> = {
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  delayed: 'bg-warning text-warning-foreground',
  info: 'bg-info text-info-foreground',
  destructive: 'bg-destructive/10 text-destructive-ink',
  muted: 'bg-muted text-muted-foreground',
  waiting: 'bg-muted text-muted-foreground'
};

// A bare status edge, for a tile that already says its status in a chip: TONE_ROW's strip without
// the row tint. Pair it with a `border-l-4`.
export const TONE_EDGE: Record<StatusTone, string> = {
  success: 'border-l-success',
  warning: 'border-l-warning',
  delayed: 'border-l-warning',
  info: 'border-l-info',
  destructive: 'border-l-destructive',
  muted: 'border-l-transparent',
  waiting: 'border-l-transparent'
};

// Whole-card emphasis for "already submitted" entities: a border in the status colour and a faint
// ring, in place of the old 4px glow.
export const TONE_BORDER: Record<StatusTone, string> = {
  success: 'border-success ring-2 ring-success/25',
  warning: 'border-warning ring-2 ring-warning/25',
  delayed: 'border-warning ring-2 ring-warning/25',
  info: 'border-info ring-2 ring-info/25',
  destructive: 'border-destructive ring-2 ring-destructive/25',
  muted: 'border-border',
  waiting: 'border-border'
};

// The same emphasis for a table row: a strip along the left edge, since a ring reads oddly inside
// a bordered table.
export const TONE_ROW: Record<StatusTone, string> = {
  success: 'border-l-4 border-l-success bg-success/5',
  warning: 'border-l-4 border-l-warning bg-warning/5',
  delayed: 'border-l-4 border-l-warning bg-warning/5',
  info: 'border-l-4 border-l-info bg-info/5',
  destructive: 'border-l-4 border-l-destructive bg-destructive/5',
  muted: 'border-l-4 border-l-transparent',
  waiting: 'border-l-4 border-l-transparent'
};

// "Sent to client" beats the failures beats "in hopper" beats everything else, since that's the
// status an analyst most needs to notice before re-submitting the same channel/video.
const WORST_FIRST: readonly SubmissionStatus[] = [
  'sent to client',
  'delivery failed',
  'not sent - removed',
  'in hopper'
];

export function worstStatus(statuses: readonly string[]): string | null {
  const set = new Set(statuses);
  return WORST_FIRST.find((status) => set.has(status)) ?? statuses[0] ?? null;
}

// Risk level → Badge variant. High is the only one that earns colour.
export function riskVariant(risk: string): 'destructive' | 'secondary' | 'outline' {
  const normalized = risk.toLowerCase();
  if (normalized === 'high') {
    return 'destructive';
  }

  return normalized === 'medium' ? 'secondary' : 'outline';
}
